/**
 * Gym Owner Dashboard — org_pro tier.
 *
 * Full-featured gym management: members, analytics, challenges, battles,
 * announcements, TV mode link. All real Supabase data, no mock data.
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, SafeAreaView, TextInput,
  TouchableOpacity, Alert, Platform, Modal, ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/lib/supabase';
import {
  fetchGymMembers, filterMembers, computeEngagementStats,
  fetchAnnouncements, postAnnouncement, removeMember as removeMemberApi,
  fetchGymChallenges, createGymChallenge, fetchGymBattleStats,
  type GymMember, type MemberFilter, type EngagementStats,
  type GymAnnouncement, type GymChallengeInfo, type GymBattleStats,
} from '@/lib/gymOwnerDashboard';
import { fetchGymBattleLeaderboard, type GymBattleRow } from '@/lib/gymBattle';
import { TYPO } from '@/constants/typography';
import { captureError } from '@/lib/errorReporting';
import TutorialOverlay from '@/components/TutorialOverlay';

/* ── Tabs ────────────────────────────────────────────────── */

const TABS = ['Members', 'Analytics', 'Challenges', 'Battles', 'Announcements', 'Settings'] as const;
type Tab = (typeof TABS)[number];

const FRESHNESS_COLORS: Record<string, string> = {
  fresh: '#22c55e', active: '#22c55e', aging: '#eab308', stale: '#f97316',
  expired: '#ef4444', unranked: '#64748b', unknown: '#64748b',
};

const MEMBER_FILTERS: { key: MemberFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'new', label: 'New' },
  { key: 'at_risk', label: 'At Risk' },
];

const CHALLENGE_TEMPLATES = [
  { title: 'Deadlift Month', desc: 'Heaviest deadlift wins. One month to set your PR.', metric: 'max_lift' },
  { title: 'Most Workouts This Week', desc: 'Log the most workouts in 7 days.', metric: 'workout_count' },
  { title: 'PR Challenge', desc: 'Set the most PRs across any exercises this month.', metric: 'pr_count' },
  { title: 'Volume War', desc: 'Accumulate the most total volume (weight × reps).', metric: 'total_volume' },
  { title: 'Consistency King', desc: 'Most consecutive workout days.', metric: 'streak' },
];

/* ── Component ───────────────────────────────────────────── */

export default function OrgDashboard() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { profile } = useAuth();
  const { tier } = useSubscription();
  const router = useRouter();

  const [tab, setTab] = useState<Tab>('Members');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data
  const [members, setMembers] = useState<GymMember[]>([]);
  const [announcements, setAnnouncements] = useState<GymAnnouncement[]>([]);
  const [challenges, setChallenges] = useState<GymChallengeInfo[]>([]);
  const [battleStats, setBattleStats] = useState<GymBattleStats>({ myGym: null, rivals: [] });
  const [gymBattleRows, setGymBattleRows] = useState<GymBattleRow[]>([]);

  // Org/gym identity
  const [orgId, setOrgId] = useState<string | null>(null);
  const [gymId, setGymId] = useState<string | null>(null);
  const [gymName, setGymName] = useState('');

  // Filters & search
  const [search, setSearch] = useState('');
  const [memberFilter, setMemberFilter] = useState<MemberFilter>('all');

  // Announcements
  const [newAnnouncementText, setNewAnnouncementText] = useState('');
  const [postingAnnouncement, setPostingAnnouncement] = useState(false);

  // Challenges
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);
  const [newChallengeTitle, setNewChallengeTitle] = useState('');
  const [newChallengeDesc, setNewChallengeDesc] = useState('');
  const [newChallengeStart, setNewChallengeStart] = useState('');
  const [newChallengeEnd, setNewChallengeEnd] = useState('');
  const [creatingChallenge, setCreatingChallenge] = useState(false);

  // Member detail
  const [selectedMember, setSelectedMember] = useState<GymMember | null>(null);

  // Settings
  const [editGymName, setEditGymName] = useState('');
  const [editGymDesc, setEditGymDesc] = useState('');

  // ── Paywall gate ──────────────────────────────────────
  if (tier !== 'org_pro') {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 }}>
          <Text style={{ fontSize: 48, marginBottom: 16 }}>🏋️</Text>
          <Text style={{ color: c.text, fontSize: 20, fontWeight: '800', textAlign: 'center', marginBottom: 12 }}>
            Club Owner Dashboard
          </Text>
          <Text style={{ color: c.textSecondary, fontSize: 15, textAlign: 'center', lineHeight: 22, marginBottom: 24 }}>
            This dashboard is available for Club (org_pro) subscribers. Manage your gym members, run challenges, and track engagement.
          </Text>
          <TouchableOpacity
            style={{ backgroundColor: c.accent, borderRadius: 2, paddingHorizontal: 28, paddingVertical: 14 }}
            onPress={() => router.push('/paywall' as any)}
          >
            <Text style={{ color: c.background, fontSize: 16, fontWeight: '700' }}>View Plans</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={{ marginTop: 16, paddingVertical: 10 }}
            onPress={() => { if (router.canGoBack()) router.back(); else router.replace('/'); }}
          >
            <Text style={{ color: c.textTertiary, fontSize: 14, fontWeight: '600' }}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // ── Data loading ──────────────────────────────────────
  const loadData = useCallback(async (isRefresh = false) => {
    if (!profile?.id) return;
    if (!isRefresh) setLoading(true);

    try {
      // Resolve org
      let resolvedOrgId = orgId;
      let resolvedGymId = gymId || profile?.gym_id || (profile as any)?.home_gym_id || null;
      let resolvedGymName = gymName || profile?.home_gym_name || '';

      if (!resolvedOrgId) {
        const { data: orgs } = await supabase
          .from('organizations')
          .select('id, name, slug')
          .eq('created_by', profile.id)
          .limit(1);

        if (orgs?.[0]) {
          resolvedOrgId = orgs[0].id;
          if (orgs[0].name) resolvedGymName = orgs[0].name;
          setOrgId(resolvedOrgId);
        }
      }

      setGymId(resolvedGymId);
      setGymName(resolvedGymName);
      setEditGymName(resolvedGymName);

      // Fetch members
      const { members: memberData } = await fetchGymMembers(
        resolvedGymId, resolvedOrgId, resolvedGymName,
      );
      setMembers(memberData);

      // Fetch announcements
      if (resolvedGymId || resolvedOrgId) {
        const { announcements: annData } = await fetchAnnouncements(
          resolvedGymId || resolvedOrgId || '',
        );
        setAnnouncements(annData);
      }

      // Fetch challenges
      if (resolvedGymId) {
        const { challenges: chData } = await fetchGymChallenges(resolvedGymId);
        setChallenges(chData);
      }

      // Fetch battle stats
      if (resolvedGymId) {
        const { stats } = await fetchGymBattleStats(resolvedGymId, resolvedGymName);
        setBattleStats(stats);
      }

      // Fetch gym battle leaderboard
      const { rows: battleRows } = await fetchGymBattleLeaderboard(supabase);
      setGymBattleRows(battleRows);

    } catch (err) {
      captureError(err, { context: 'org-dashboard.loadData' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [profile?.id, orgId, gymId, gymName]);

  useEffect(() => { loadData(); }, [loadData]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    loadData(true);
  }, [loadData]);

  // ── Derived data ──────────────────────────────────────
  const engagementStats = useMemo(() => computeEngagementStats(members), [members]);

  const filteredMembers = useMemo(() => {
    let list = filterMembers(members, memberFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.name.toLowerCase().includes(q) || m.handle.toLowerCase().includes(q),
      );
    }
    return list;
  }, [members, memberFilter, search]);

  // ── Actions ───────────────────────────────────────────
  const handleRemoveMember = (member: GymMember) => {
    Alert.alert(
      'Remove Member',
      `Remove ${member.name} from your gym? They can rejoin later.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove', style: 'destructive', onPress: async () => {
            const { error } = await removeMemberApi(orgId, gymId, member.id);
            if (error) {
              Alert.alert('Error', error);
            } else {
              setMembers(prev => prev.filter(m => m.id !== member.id));
            }
          },
        },
      ],
    );
  };

  const handlePostAnnouncement = async () => {
    if (!newAnnouncementText.trim() || !profile?.id) return;
    setPostingAnnouncement(true);
    const { announcement, error } = await postAnnouncement(
      gymId || orgId || '',
      profile.id,
      profile.display_name || profile.handle || 'Admin',
      newAnnouncementText.trim(),
    );
    if (announcement) {
      setAnnouncements(prev => [announcement, ...prev]);
      setNewAnnouncementText('');
    } else if (error) {
      Alert.alert('Error', error);
    }
    setPostingAnnouncement(false);
  };

  const handleCreateChallenge = async () => {
    if (!newChallengeTitle.trim() || !gymId) return;
    setCreatingChallenge(true);
    const start = newChallengeStart || new Date().toISOString().slice(0, 10);
    const end = newChallengeEnd || new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    const { id, error } = await createGymChallenge(
      gymId, newChallengeTitle.trim(), newChallengeDesc.trim(), start, end,
    );
    if (id) {
      setChallenges(prev => [{
        id,
        title: newChallengeTitle.trim(),
        description: newChallengeDesc.trim(),
        start_date: start,
        end_date: end,
        status: 'active',
        participant_count: 0,
        metric: 'general',
        leaderboard: [],
      }, ...prev]);
      setShowCreateChallenge(false);
      setNewChallengeTitle('');
      setNewChallengeDesc('');
      setNewChallengeStart('');
      setNewChallengeEnd('');
    } else if (error) {
      Alert.alert('Error', error);
    }
    setCreatingChallenge(false);
  };

  const applyTemplate = (template: typeof CHALLENGE_TEMPLATES[number]) => {
    setNewChallengeTitle(template.title);
    setNewChallengeDesc(template.desc);
    const now = new Date();
    setNewChallengeStart(now.toISOString().slice(0, 10));
    setNewChallengeEnd(new Date(now.getTime() + 30 * 86400000).toISOString().slice(0, 10));
    setShowCreateChallenge(true);
  };

  // ── Formatters ────────────────────────────────────────
  const formatNum = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return String(n);
  };

  const daysSince = (dateStr: string | null) => {
    if (!dateStr) return null;
    return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  };

  // ── Tab renderers ─────────────────────────────────────

  const renderMembers = () => (
    <View>
      {/* Stats bar */}
      <View style={styles.statsRow}>
        <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.statValue, { color: c.accent }]}>{members.length}</Text>
          <Text style={[styles.statLabel, { color: c.textTertiary }]}>Total</Text>
        </View>
        <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.statValue, { color: '#22c55e' }]}>{engagementStats.activeMembers30d}</Text>
          <Text style={[styles.statLabel, { color: c.textTertiary }]}>Active</Text>
        </View>
        <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.statValue, { color: '#eab308' }]}>{engagementStats.newMembers7d}</Text>
          <Text style={[styles.statLabel, { color: c.textTertiary }]}>New (7d)</Text>
        </View>
        <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.statValue, { color: '#ef4444' }]}>{engagementStats.atRiskMembers}</Text>
          <Text style={[styles.statLabel, { color: c.textTertiary }]}>At Risk</Text>
        </View>
      </View>

      {/* Filter pills */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }} contentContainerStyle={{ gap: 6, paddingHorizontal: 16 }}>
        {MEMBER_FILTERS.map(f => (
          <TouchableOpacity
            key={f.key}
            onPress={() => setMemberFilter(f.key)}
            style={[styles.filterPill, {
              backgroundColor: memberFilter === f.key ? c.accent : c.surface,
              borderColor: memberFilter === f.key ? c.accent : c.border,
            }]}
          >
            <Text style={{
              color: memberFilter === f.key ? c.background : c.textSecondary,
              fontSize: 12, fontWeight: '700',
            }}>{f.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Search */}
      <View style={{ paddingHorizontal: 16, marginBottom: 12 }}>
        <TextInput
          style={[styles.searchInput, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border }]}
          placeholder="Search members..."
          placeholderTextColor={c.textTertiary}
          value={search}
          onChangeText={setSearch}
        />
      </View>

      {/* Member list */}
      {filteredMembers.length === 0 ? (
        <View style={{ alignItems: 'center', paddingVertical: 40 }}>
          <Text style={{ color: c.textTertiary, fontSize: 16, fontWeight: '600' }}>
            {members.length === 0 ? 'No members found' : 'No matching members'}
          </Text>
          <Text style={{ color: c.textTertiary, fontSize: 13, marginTop: 6, textAlign: 'center', paddingHorizontal: 40 }}>
            {members.length === 0
              ? 'Members who set this gym as their home gym will appear here.'
              : 'Try adjusting your search or filter.'}
          </Text>
        </View>
      ) : (
        filteredMembers.map(m => {
          const days = daysSince(m.last_active);
          const freshColor = FRESHNESS_COLORS[m.freshness_band] || FRESHNESS_COLORS.unknown;
          return (
            <TouchableOpacity
              key={m.id}
              onPress={() => setSelectedMember(m)}
              style={[styles.memberRow, { backgroundColor: c.surface, borderColor: c.border }]}
            >
              {/* Avatar */}
              <View style={[styles.avatar, { backgroundColor: c.surfaceVariant }]}>
                <Text style={{ color: c.textSecondary, fontSize: 16, fontWeight: '800' }}>
                  {m.name.charAt(0).toUpperCase()}
                </Text>
              </View>

              <View style={{ flex: 1 }}>
                <Text style={[styles.memberName, { color: c.text }]}>
                  {m.name} <Text style={{ color: c.textTertiary, fontWeight: '400', fontSize: 12 }}>{m.handle}</Text>
                </Text>
                <View style={styles.memberMeta}>
                  <Text style={{ color: c.accent, fontSize: 13, fontWeight: '700' }}>{m.strength_score || '—'}</Text>
                  <View style={[styles.freshDot, { backgroundColor: freshColor }]} />
                  <Text style={{ color: c.textTertiary, fontSize: 11 }}>{m.freshness_band}</Text>
                  <Text style={{ color: c.textTertiary, fontSize: 11 }}>
                    {days !== null ? `${days}d ago` : 'Never'}
                  </Text>
                  <Text style={{ color: c.textTertiary, fontSize: 11 }}>Joined {m.join_date}</Text>
                </View>
              </View>

              <TouchableOpacity
                onPress={() => handleRemoveMember(m)}
                style={[styles.removeBtn, { borderColor: c.error }]}
                hitSlop={8}
              >
                <Text style={{ color: c.error, fontSize: 11, fontWeight: '600' }}>Remove</Text>
              </TouchableOpacity>
            </TouchableOpacity>
          );
        })
      )}
    </View>
  );

  const renderAnalytics = () => (
    <View style={{ paddingHorizontal: 16 }}>
      {/* Key stats */}
      <View style={styles.statsRow}>
        <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.statValue, { color: c.accent }]}>{engagementStats.workoutsThisWeek}</Text>
          <Text style={[styles.statLabel, { color: c.textTertiary }]}>Workouts (7d)</Text>
        </View>
        <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.statValue, { color: c.accent }]}>{engagementStats.workoutsThisMonth}</Text>
          <Text style={[styles.statLabel, { color: c.textTertiary }]}>Workouts (30d)</Text>
        </View>
      </View>

      <View style={styles.statsRow}>
        <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.statValue, { color: c.accent }]}>{engagementStats.avgStrengthScore}</Text>
          <Text style={[styles.statLabel, { color: c.textTertiary }]}>Avg Score</Text>
        </View>
        <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.statValue, { color: c.accent }]}>{engagementStats.activePercentage}%</Text>
          <Text style={[styles.statLabel, { color: c.textTertiary }]}>Active Rate</Text>
        </View>
      </View>

      {/* At Risk Members */}
      {engagementStats.atRiskMembers > 0 && (
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: '#ef4444' + '44' }]}>
          <Text style={[styles.cardTitle, { color: '#ef4444' }]}>⚠️ AT RISK ({engagementStats.atRiskMembers})</Text>
          <Text style={{ color: c.textSecondary, fontSize: 13, marginBottom: 8 }}>
            No activity in 14+ days
          </Text>
          {filterMembers(members, 'at_risk').slice(0, 5).map(m => (
            <View key={m.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, gap: 8 }}>
              <Text style={{ color: c.text, fontSize: 14, fontWeight: '600', flex: 1 }}>{m.name}</Text>
              <Text style={{ color: '#ef4444', fontSize: 12 }}>
                {daysSince(m.last_active) !== null ? `${daysSince(m.last_active)}d inactive` : 'Never active'}
              </Text>
            </View>
          ))}
        </View>
      )}

      {/* Top Performers */}
      {engagementStats.topPerformers.length > 0 && (
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.cardTitle, { color: c.accent }]}>🏆 TOP PERFORMERS (7d Volume)</Text>
          {engagementStats.topPerformers.map((p, i) => (
            <View key={p.handle || i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6 }}>
              <Text style={{ color: i < 3 ? c.accent : c.textSecondary, fontSize: 14, fontWeight: '800', width: 28 }}>#{i + 1}</Text>
              <Text style={{ color: c.text, fontSize: 14, fontWeight: '600', flex: 1 }}>{p.name}</Text>
              <Text style={{ color: c.accent, fontSize: 14, fontWeight: '700' }}>{formatNum(p.score)} vol</Text>
            </View>
          ))}
        </View>
      )}

      {/* Workout Frequency Distribution */}
      <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
        <Text style={[styles.cardTitle, { color: c.textSecondary }]}>WORKOUT FREQUENCY (7d)</Text>
        {engagementStats.workoutDistribution.map(b => {
          const maxCount = Math.max(...engagementStats.workoutDistribution.map(d => d.count), 1);
          const pct = (b.count / maxCount) * 100;
          return (
            <View key={b.bucket} style={{ marginBottom: 10 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text style={{ color: c.text, fontSize: 13 }}>{b.bucket}</Text>
                <Text style={{ color: c.textSecondary, fontSize: 13 }}>{b.count} members</Text>
              </View>
              <View style={{ height: 8, backgroundColor: c.surfaceVariant, borderRadius: 4 }}>
                <View style={{ height: 8, backgroundColor: c.accent, borderRadius: 4, width: `${pct}%` }} />
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );

  const renderChallenges = () => (
    <View style={{ paddingHorizontal: 16 }}>
      {/* Active challenges */}
      {challenges.filter(ch => ch.status === 'active').length > 0 && (
        <>
          <Text style={[styles.sectionLabel, { color: c.accent }]}>ACTIVE CHALLENGES</Text>
          {challenges.filter(ch => ch.status === 'active').map(ch => (
            <View key={ch.id} style={[styles.card, { backgroundColor: c.surface, borderColor: c.accent + '44' }]}>
              <Text style={{ color: c.text, fontSize: 16, fontWeight: '800' }}>{ch.title}</Text>
              {ch.description ? <Text style={{ color: c.textSecondary, fontSize: 13, marginTop: 4 }}>{ch.description}</Text> : null}
              <View style={{ flexDirection: 'row', gap: 16, marginTop: 10 }}>
                <Text style={{ color: c.accent, fontSize: 12, fontWeight: '700' }}>{ch.participant_count} participants</Text>
                <Text style={{ color: c.textTertiary, fontSize: 12 }}>Ends {ch.end_date}</Text>
              </View>
            </View>
          ))}
        </>
      )}

      {/* Upcoming */}
      {challenges.filter(ch => ch.status === 'upcoming').length > 0 && (
        <>
          <Text style={[styles.sectionLabel, { color: c.textSecondary }]}>UPCOMING</Text>
          {challenges.filter(ch => ch.status === 'upcoming').map(ch => (
            <View key={ch.id} style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Text style={{ color: c.text, fontSize: 15, fontWeight: '700' }}>{ch.title}</Text>
              <Text style={{ color: c.textTertiary, fontSize: 12, marginTop: 4 }}>Starts {ch.start_date}</Text>
            </View>
          ))}
        </>
      )}

      {/* Completed */}
      {challenges.filter(ch => ch.status === 'completed').length > 0 && (
        <>
          <Text style={[styles.sectionLabel, { color: c.textTertiary }]}>COMPLETED</Text>
          {challenges.filter(ch => ch.status === 'completed').slice(0, 5).map(ch => (
            <View key={ch.id} style={[styles.card, { backgroundColor: c.surface, borderColor: c.border, opacity: 0.7 }]}>
              <Text style={{ color: c.text, fontSize: 14, fontWeight: '600' }}>{ch.title}</Text>
              <Text style={{ color: c.textTertiary, fontSize: 12, marginTop: 2 }}>
                {ch.participant_count} participants · Ended {ch.end_date}
              </Text>
            </View>
          ))}
        </>
      )}

      {/* Templates */}
      <Text style={[styles.sectionLabel, { color: c.textSecondary, marginTop: 20 }]}>CHALLENGE TEMPLATES</Text>
      {CHALLENGE_TEMPLATES.map((t, i) => (
        <TouchableOpacity
          key={i}
          onPress={() => applyTemplate(t)}
          style={[styles.card, { backgroundColor: c.surfaceVariant, borderColor: c.border }]}
        >
          <Text style={{ color: c.text, fontSize: 14, fontWeight: '700' }}>{t.title}</Text>
          <Text style={{ color: c.textTertiary, fontSize: 12, marginTop: 2 }}>{t.desc}</Text>
          <Text style={{ color: c.accent, fontSize: 11, fontWeight: '700', marginTop: 6 }}>Use Template →</Text>
        </TouchableOpacity>
      ))}

      {/* Create custom */}
      <TouchableOpacity
        onPress={() => setShowCreateChallenge(true)}
        style={[styles.actionBtn, { backgroundColor: c.accent }]}
      >
        <Text style={{ color: c.background, fontWeight: '700', fontSize: 15 }}>+ Create Custom Challenge</Text>
      </TouchableOpacity>

      {/* Create challenge form */}
      {showCreateChallenge && (
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.accent + '44', marginTop: 12 }]}>
          <Text style={[styles.cardTitle, { color: c.accent }]}>NEW CHALLENGE</Text>
          <TextInput
            style={[styles.input, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border }]}
            placeholder="Challenge title"
            placeholderTextColor={c.textTertiary}
            value={newChallengeTitle}
            onChangeText={setNewChallengeTitle}
          />
          <TextInput
            style={[styles.input, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border, height: 60, textAlignVertical: 'top' }]}
            placeholder="Description"
            placeholderTextColor={c.textTertiary}
            value={newChallengeDesc}
            onChangeText={setNewChallengeDesc}
            multiline
          />
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TextInput
              style={[styles.input, { flex: 1, backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border }]}
              placeholder="Start (YYYY-MM-DD)"
              placeholderTextColor={c.textTertiary}
              value={newChallengeStart}
              onChangeText={setNewChallengeStart}
            />
            <TextInput
              style={[styles.input, { flex: 1, backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border }]}
              placeholder="End (YYYY-MM-DD)"
              placeholderTextColor={c.textTertiary}
              value={newChallengeEnd}
              onChangeText={setNewChallengeEnd}
            />
          </View>
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
            <TouchableOpacity
              onPress={handleCreateChallenge}
              disabled={creatingChallenge || !newChallengeTitle.trim()}
              style={[styles.actionBtn, { backgroundColor: c.accent, flex: 1 }]}
            >
              {creatingChallenge
                ? <ActivityIndicator size="small" color={c.background} />
                : <Text style={{ color: c.background, fontWeight: '700' }}>Create</Text>}
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => setShowCreateChallenge(false)}
              style={[styles.actionBtn, { backgroundColor: c.surfaceVariant, flex: 1 }]}
            >
              <Text style={{ color: c.textSecondary, fontWeight: '700' }}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Empty state */}
      {challenges.length === 0 && !showCreateChallenge && (
        <View style={{ alignItems: 'center', paddingVertical: 30 }}>
          <Text style={{ color: c.textTertiary, fontSize: 14, textAlign: 'center' }}>
            No challenges yet. Use a template above or create a custom one.
          </Text>
        </View>
      )}
    </View>
  );

  const renderBattles = () => (
    <View style={{ paddingHorizontal: 16 }}>
      {/* My gym */}
      {battleStats.myGym ? (
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.accent + '44' }]}>
          <Text style={[styles.cardTitle, { color: c.accent }]}>YOUR GYM</Text>
          <Text style={{ color: c.text, fontSize: 20, fontWeight: '900' }}>{battleStats.myGym.name}</Text>
          <View style={{ flexDirection: 'row', gap: 20, marginTop: 12 }}>
            <View style={{ alignItems: 'center' }}>
              <Text style={{ color: c.accent, fontSize: 28, fontWeight: '900' }}>#{battleStats.myGym.rank}</Text>
              <Text style={{ color: c.textTertiary, fontSize: 11 }}>Rank</Text>
            </View>
            <View style={{ alignItems: 'center' }}>
              <Text style={{ color: c.text, fontSize: 22, fontWeight: '800' }}>{battleStats.myGym.avg_strength}</Text>
              <Text style={{ color: c.textTertiary, fontSize: 11 }}>Avg Score</Text>
            </View>
            <View style={{ alignItems: 'center' }}>
              <Text style={{ color: c.text, fontSize: 22, fontWeight: '800' }}>{battleStats.myGym.member_count}</Text>
              <Text style={{ color: c.textTertiary, fontSize: 11 }}>Members</Text>
            </View>
          </View>
        </View>
      ) : (
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={{ color: c.textTertiary, fontSize: 14, textAlign: 'center' }}>
            Your gym doesn't have ranked members yet. Members need strength scores to appear in battles.
          </Text>
        </View>
      )}

      {/* Rival gyms */}
      <Text style={[styles.sectionLabel, { color: c.textSecondary, marginTop: 16 }]}>GYM RANKINGS</Text>
      {gymBattleRows.length > 0 ? (
        gymBattleRows.map((g, i) => {
          const isMyGym = g.gym_id === gymId;
          return (
            <View
              key={g.gym_id}
              style={[styles.card, {
                backgroundColor: isMyGym ? c.accent + '12' : c.surface,
                borderColor: isMyGym ? c.accent + '44' : c.border,
              }]}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Text style={{ color: i < 3 ? c.accent : c.textSecondary, fontSize: 18, fontWeight: '900', width: 36 }}>#{i + 1}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={{ color: c.text, fontSize: 15, fontWeight: '700' }}>
                    {g.gym_name} {isMyGym ? '(You)' : ''}
                  </Text>
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                  <Text style={{ color: c.accent, fontSize: 16, fontWeight: '800' }}>{g.avg_strength}</Text>
                  <Text style={{ color: c.textTertiary, fontSize: 10 }}>{g.member_count} members</Text>
                </View>
              </View>
            </View>
          );
        })
      ) : (
        <View style={{ alignItems: 'center', paddingVertical: 20 }}>
          <Text style={{ color: c.textTertiary, fontSize: 14 }}>No gym battle data available yet.</Text>
        </View>
      )}
    </View>
  );

  const renderAnnouncements = () => (
    <View style={{ paddingHorizontal: 16 }}>
      {/* Compose */}
      <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
        <Text style={[styles.cardTitle, { color: c.textSecondary }]}>POST ANNOUNCEMENT</Text>
        <TextInput
          style={[styles.input, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border, minHeight: 80, textAlignVertical: 'top' }]}
          placeholder="Write an announcement for your members..."
          placeholderTextColor={c.textTertiary}
          value={newAnnouncementText}
          onChangeText={setNewAnnouncementText}
          multiline
        />
        <TouchableOpacity
          onPress={handlePostAnnouncement}
          disabled={postingAnnouncement || !newAnnouncementText.trim()}
          style={[styles.actionBtn, {
            backgroundColor: newAnnouncementText.trim() ? c.accent : c.surfaceVariant,
            marginTop: 8,
          }]}
        >
          {postingAnnouncement
            ? <ActivityIndicator size="small" color={c.background} />
            : <Text style={{ color: newAnnouncementText.trim() ? c.background : c.textTertiary, fontWeight: '700', fontSize: 15 }}>
                Post to All Members
              </Text>}
        </TouchableOpacity>
      </View>

      {/* Announcement list */}
      {announcements.length > 0 ? (
        <>
          <Text style={[styles.sectionLabel, { color: c.textSecondary, marginTop: 16 }]}>RECENT ANNOUNCEMENTS</Text>
          {announcements.map(a => (
            <View key={a.id} style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Text style={{ color: c.text, fontSize: 14, lineHeight: 20 }}>{a.text}</Text>
              <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                <Text style={{ color: c.textTertiary, fontSize: 11 }}>{a.author_name}</Text>
                <Text style={{ color: c.textTertiary, fontSize: 11 }}>·</Text>
                <Text style={{ color: c.textTertiary, fontSize: 11 }}>
                  {new Date(a.created_at).toLocaleDateString()}
                </Text>
              </View>
            </View>
          ))}
        </>
      ) : (
        <View style={{ alignItems: 'center', paddingVertical: 30 }}>
          <Text style={{ color: c.textTertiary, fontSize: 14, textAlign: 'center' }}>
            No announcements yet. Post one above to reach all your members.
          </Text>
        </View>
      )}
    </View>
  );

  const renderSettings = () => (
    <View style={{ paddingHorizontal: 16 }}>
      <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
        <Text style={[styles.cardTitle, { color: c.textSecondary }]}>GYM PROFILE</Text>
        <Text style={{ color: c.textTertiary, fontSize: 11, marginBottom: 4 }}>GYM NAME</Text>
        <TextInput
          style={[styles.input, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border }]}
          value={editGymName}
          onChangeText={setEditGymName}
        />
        <Text style={{ color: c.textTertiary, fontSize: 11, marginBottom: 4 }}>DESCRIPTION</Text>
        <TextInput
          style={[styles.input, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border, height: 80, textAlignVertical: 'top' }]}
          value={editGymDesc}
          onChangeText={setEditGymDesc}
          multiline
        />
        <TouchableOpacity
          onPress={async () => {
            if (orgId) {
              await supabase.from('organizations').update({ name: editGymName, description: editGymDesc }).eq('id', orgId);
              setGymName(editGymName);
              Alert.alert('Saved', 'Gym profile updated.');
            }
          }}
          style={[styles.actionBtn, { backgroundColor: c.accent }]}
        >
          <Text style={{ color: c.background, fontWeight: '700' }}>Save Settings</Text>
        </TouchableOpacity>
      </View>

      {/* TV Mode link */}
      {gymId && (
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.cardTitle, { color: c.textSecondary }]}>TV DISPLAY MODE</Text>
          <Text style={{ color: c.textSecondary, fontSize: 13, marginBottom: 12 }}>
            Display a live leaderboard on a TV in your gym.
          </Text>
          <TouchableOpacity
            onPress={() => router.push(`/tv/${gymId}` as any)}
            style={[styles.actionBtn, { backgroundColor: c.accent }]}
          >
            <Text style={{ color: c.background, fontWeight: '700' }}>Open TV Mode</Text>
          </TouchableOpacity>
        </View>
      )}


    </View>
  );

  const renderTab = () => {
    switch (tab) {
      case 'Members': return renderMembers();
      case 'Analytics': return renderAnalytics();
      case 'Challenges': return renderChallenges();
      case 'Battles': return renderBattles();
      case 'Announcements': return renderAnnouncements();
      case 'Settings': return renderSettings();
      default: return null;
    }
  };

  // ── Loading ───────────────────────────────────────────
  if (loading) {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color={c.accent} />
        </View>
      </SafeAreaView>
    );
  }

  // ── Main render ───────────────────────────────────────
  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      <ScrollView
        contentContainerStyle={styles.container}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />}
      >
        {/* Header */}
        <View style={[styles.headerBar, { backgroundColor: c.accent + '10' }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <View style={[styles.logoBox, { backgroundColor: c.accent + '22', borderColor: c.accent + '44' }]}>
              <Text style={{ color: c.accent, fontSize: 14, fontWeight: '800' }}>VS</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.headerTitle, { color: c.text }]}>{gymName || 'Club Dashboard'}</Text>
              <Text style={{ color: c.textTertiary, fontSize: 11 }}>
                {members.length} members · {engagementStats.activePercentage}% active
              </Text>
            </View>
          </View>
        </View>

        {/* Tabs */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.tabScroll}
          contentContainerStyle={{ gap: 4, paddingHorizontal: 16 }}
        >
          {TABS.map(t => (
            <TouchableOpacity
              key={t}
              onPress={() => setTab(t)}
              style={[styles.tabItem, tab === t && { borderBottomColor: c.accent, borderBottomWidth: 2 }]}
            >
              <Text style={{ color: tab === t ? c.accent : c.textSecondary, fontSize: 13, fontWeight: '600' }}>{t}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* Content */}
        {renderTab()}
      </ScrollView>

      {/* Member detail modal */}
      <Modal visible={!!selectedMember} transparent animationType="fade" onRequestClose={() => setSelectedMember(null)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: c.surface, borderColor: c.border }]}>
            <TouchableOpacity onPress={() => setSelectedMember(null)} style={styles.modalClose}>
              <Text style={{ color: c.textTertiary, fontSize: 18, fontWeight: '700' }}>✕</Text>
            </TouchableOpacity>

            {selectedMember && (() => {
              const rank = [...members].sort((a, b) => b.strength_score - a.strength_score).findIndex(m => m.id === selectedMember.id) + 1;
              const days = daysSince(selectedMember.last_active);
              return (
                <>
                  <View style={[styles.modalAvatar, { backgroundColor: c.accent + '22' }]}>
                    <Text style={{ color: c.accent, fontSize: 28, fontWeight: '800' }}>
                      {selectedMember.name.charAt(0).toUpperCase()}
                    </Text>
                  </View>
                  <Text style={{ color: c.text, fontSize: 18, fontWeight: '800', textAlign: 'center' }}>{selectedMember.name}</Text>
                  <Text style={{ color: c.textSecondary, fontSize: 14, textAlign: 'center', marginBottom: 16 }}>{selectedMember.handle}</Text>

                  <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginBottom: 16 }}>
                    <View style={{ alignItems: 'center' }}>
                      <Text style={{ color: c.accent, fontSize: 18, fontWeight: '800' }}>#{rank}</Text>
                      <Text style={{ color: c.textTertiary, fontSize: 11 }}>Rank</Text>
                    </View>
                    <View style={{ alignItems: 'center' }}>
                      <Text style={{ color: c.accent, fontSize: 18, fontWeight: '800' }}>{selectedMember.strength_score || '—'}</Text>
                      <Text style={{ color: c.textTertiary, fontSize: 11 }}>Score</Text>
                    </View>
                    <View style={{ alignItems: 'center' }}>
                      <Text style={{ color: c.text, fontSize: 14, fontWeight: '600' }}>
                        {days !== null ? `${days}d ago` : 'Never'}
                      </Text>
                      <Text style={{ color: c.textTertiary, fontSize: 11 }}>Last Active</Text>
                    </View>
                  </View>

                  <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginBottom: 20 }}>
                    <View style={{ alignItems: 'center' }}>
                      <Text style={{ color: c.text, fontSize: 16, fontWeight: '700' }}>{selectedMember.workout_count_7d}</Text>
                      <Text style={{ color: c.textTertiary, fontSize: 11 }}>Workouts (7d)</Text>
                    </View>
                    <View style={{ alignItems: 'center' }}>
                      <Text style={{ color: c.text, fontSize: 16, fontWeight: '700' }}>{formatNum(selectedMember.total_volume_7d)}</Text>
                      <Text style={{ color: c.textTertiary, fontSize: 11 }}>Volume (7d)</Text>
                    </View>
                    <View style={{ alignItems: 'center' }}>
                      <Text style={{ color: c.text, fontSize: 14, fontWeight: '600' }}>{selectedMember.join_date}</Text>
                      <Text style={{ color: c.textTertiary, fontSize: 11 }}>Joined</Text>
                    </View>
                  </View>

                  <View style={{ flexDirection: 'row', gap: 10 }}>
                    <TouchableOpacity
                      onPress={() => {
                        const handle = selectedMember.handle.replace(/^@/, '');
                        setSelectedMember(null);
                        if (handle) router.push(`/u/${handle}` as any);
                      }}
                      style={{ flex: 1, backgroundColor: c.accent, borderRadius: 2, paddingVertical: 12, alignItems: 'center' }}
                    >
                      <Text style={{ color: c.background, fontWeight: '700' }}>View Profile</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => handleRemoveMember(selectedMember)}
                      style={{ flex: 1, borderWidth: 1, borderColor: c.error, borderRadius: 2, paddingVertical: 12, alignItems: 'center' }}
                    >
                      <Text style={{ color: c.error, fontWeight: '700' }}>Remove</Text>
                    </TouchableOpacity>
                  </View>
                </>
              );
            })()}
          </View>
        </View>
      </Modal>
      <TutorialOverlay screenKey="club_upgrade" />
    </SafeAreaView>
  );
}

/* ── Styles ──────────────────────────────────────────────── */

const styles = StyleSheet.create({
  safe: { flex: 1 },
  container: { paddingBottom: 40 },

  // Header
  headerBar: { padding: 16, paddingBottom: 12 },
  logoBox: { width: 44, height: 44, borderRadius: 2, borderWidth: 1, alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  headerTitle: { fontSize: 20, fontFamily: TYPO.heading.bold, letterSpacing: 0.5 },

  // Stats
  statsRow: { flexDirection: 'row', gap: 8, paddingHorizontal: 16, marginBottom: 10 },
  statCard: { flex: 1, borderRadius: 2, borderWidth: 1, padding: 10, alignItems: 'center' },
  statValue: { fontSize: 20, fontFamily: TYPO.heading.bold },
  statLabel: { fontSize: 10, fontFamily: TYPO.body.medium, marginTop: 2, textAlign: 'center' },

  // Tabs
  tabScroll: { marginBottom: 16 },
  tabItem: { paddingVertical: 8, paddingHorizontal: 14 },

  // Search/Input
  searchInput: { borderWidth: 1, borderRadius: 2, padding: 12, fontSize: 14 },
  input: { borderWidth: 1, borderRadius: 2, padding: 12, fontSize: 14, marginBottom: 8 },

  // Filter pills
  filterPill: { borderWidth: 1, borderRadius: 2, paddingHorizontal: 14, paddingVertical: 6 },

  // Cards
  card: { borderWidth: 1, borderRadius: 2, padding: 14, marginBottom: 10 },
  cardTitle: { fontSize: 11, fontWeight: '700', letterSpacing: 1, marginBottom: 8 },
  sectionLabel: { fontSize: 11, fontWeight: '700', letterSpacing: 1, marginBottom: 8, marginTop: 12 },

  // Members
  memberRow: { flexDirection: 'row', alignItems: 'center', borderRadius: 2, borderWidth: 1, padding: 12, marginBottom: 8, marginHorizontal: 16, gap: 10 },
  avatar: { width: 40, height: 40, borderRadius: 4, alignItems: 'center', justifyContent: 'center' },
  memberName: { fontSize: 14, fontFamily: TYPO.body.bold },
  memberMeta: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 3 },
  freshDot: { width: 7, height: 7, borderRadius: 4 },
  removeBtn: { borderWidth: 1, borderRadius: 4, paddingHorizontal: 10, paddingVertical: 4 },

  // Action buttons
  actionBtn: { borderRadius: 2, padding: 14, alignItems: 'center' },

  // Modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  modalContent: { borderWidth: 1, borderRadius: 2, padding: 24, width: '100%', maxWidth: 360 },
  modalClose: { position: 'absolute', top: 12, right: 12, zIndex: 1 },
  modalAvatar: { width: 64, height: 64, borderRadius: 4, alignSelf: 'center', alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
});
